import { LOGIN_URL } from "@/urls";
import type { Location } from "react-router";

export const LOGIN_REDIRECT_TARGET_KEY = "loginRedirectTarget";
// A target older than this is a leftover from an abandoned sign-in, not the
// page the user is trying to reach now.
const MAX_AGE_MS = 15 * 60 * 1000;

export function locationToPath(location: Pick<Location, "pathname" | "search" | "hash">): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function storeLoginRedirectTarget(
  location: Pick<Location, "pathname" | "search" | "hash">
): void {
  if (location.pathname === LOGIN_URL) {
    return;
  }
  try {
    sessionStorage.setItem(
      LOGIN_REDIRECT_TARGET_KEY,
      JSON.stringify({ path: locationToPath(location), storedAt: Date.now() })
    );
  } catch {
    return;
  }
}

// Reads without consuming. The sign-in flow can pass through the login page
// more than once (a full-page provider redirect, a re-check while the user
// record loads), and each pass must still find the target. It is cleared once
// an authenticated page has rendered, on logout, or when it goes stale. The
// target is per tab (sessionStorage) so two tabs signing in at once cannot
// overwrite or erase each other's destination.
export function peekLoginRedirectTarget(): string | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_TARGET_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as { path?: unknown }).path !== "string" ||
      typeof (parsed as { storedAt?: unknown }).storedAt !== "number"
    ) {
      sessionStorage.removeItem(LOGIN_REDIRECT_TARGET_KEY);
      return null;
    }
    const { path, storedAt } = parsed as { path: string; storedAt: number };
    if (Date.now() - storedAt > MAX_AGE_MS || path === LOGIN_URL || !isSameSitePath(path)) {
      sessionStorage.removeItem(LOGIN_REDIRECT_TARGET_KEY);
      return null;
    }
    return path;
  } catch {
    return null;
  }
}

// A path for this site only: one leading slash. "//host" and "/\\host" are
// read by browsers as another origin.
function isSameSitePath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

export function clearLoginRedirectTarget(): void {
  try {
    sessionStorage.removeItem(LOGIN_REDIRECT_TARGET_KEY);
  } catch {
    return;
  }
}
